<template>
	<div
		class="site-columns"
		:class="{ 'site-columns--full-width': isFullWidth }"
	>
		<div class="site-columns__inner" :style="gridStyle">
			<div
				v-for="column in columns"
				:key="column.id"
				class="site-columns__column"
				:style="columnStyle(column)"
			>
				<site-block-renderer
					v-for="childBlock in column.blocks"
					:key="childBlock.id"
					:block="childBlock"
				/>
			</div>
		</div>
	</div>
</template>

<script>
/**
 * SiteColumnsLayout.vue
 * ─────────────────────
 * Public-facing read-only columns layout for the Website SPA.
 * No Vuex, no drag-and-drop, no admin UI.
 *
 * Note: SiteBlockRenderer is registered globally in index.js to avoid
 * a circular dependency (block renderer renders columns, columns render
 * child blocks via the renderer).
 */

export default {
	name: 'SiteColumnsLayout',

	props: {
		layoutRatio: { type: String, default: '1fr 1fr' },
		gap: { type: String, default: '1rem' },
		columns: { type: Array, default: function () { return []; } },
		fullWidth: { type: [Boolean, String], default: false },
		stretchContent: { type: [Boolean, String], default: false },
		verticalAlign: { type: String, default: 'top' }
	},

	inject: {
		sitePageWidth: { default: '1000px' }
	},

	computed: {
		isFullWidth: function () {
			return this.fullWidth === true || this.fullWidth === 'true';
		},

		/**
		 * Inner grid style — holds the column grid with spacing.
		 * Outer background, border, radius and padding are style traits
		 * applied by SiteBlockRenderer's wrapper, not by this component.
		 */
		gridStyle: function () {
			return {
				display: 'grid',
				gridTemplateColumns: this.layoutRatio,
				gap: this.gap
			};
		}
	},

	methods: {
		/**
		 * Per-column styles for vertical and horizontal alignment.
		 * Each column becomes a flex-column container so justify-content
		 * controls vertical positioning of child blocks.
		 */
		columnStyle: function (column) {
			var s = {
				display: 'flex',
				flexDirection: 'column'
			};

			var vAlign = column.alignSelf || ({ middle: 'center', bottom: 'end' }[this.verticalAlign] || 'stretch');
			if (vAlign === 'start') {
				s.justifyContent = 'flex-start';
			} else if (vAlign === 'center') {
				s.justifyContent = 'center';
			} else if (vAlign === 'end') {
				s.justifyContent = 'flex-end';
			} else {
				s.justifyContent = 'flex-start';
			}

			if (column.justifySelf) {
				s.alignItems = column.justifySelf === 'start' ? 'flex-start'
					: column.justifySelf === 'end' ? 'flex-end'
					: column.justifySelf === 'center' ? 'center'
					: column.justifySelf === 'stretch' ? 'stretch'
					: undefined;
			}

			return s;
		}
	}
};
</script>

<style scoped>
.site-columns {
	width: 100%;
	position: relative;
}


.site-columns__inner {
	width: 100%;
}

.site-columns__column {
	min-width: 0;
}

@media (max-width: 767px) {
	.site-columns__inner {
		grid-template-columns: 1fr !important;
	}
}
</style>
